import React, { useState, useEffect, useRef, useMemo } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import {
  AppHeader,
  SettingsModal,
  BgAnimate,
  StatisticsModal,
  AppIcon,
  HeaderIcons,
} from '../shared';
import { isMonthlyFreeGame } from '../../config/monthlyFreeGames';
import { BurgerMenu } from '../menu/Menu';
import MenuPageWrapper from '../menu/MenuPageWrapper';
import HowToPlayPage from '../menu/HowToPlayPage';
import PremiumPage from '../menu/PremiumPage';
import HelpPage from '../menu/HelpPage';
import '../../App.css';
import '../../styles/lobby.css';
import { avatars } from '../../assets/avatars';
import { useToast } from '../shared/ToastContext';
import { appVariant, ALL_GAMES, config } from '../../config';
import { GameConfig } from '../../config/types';
import RoomSessionStorage from '../../utils/sessionStorage';
import { getMenuGameLinks } from '../../config/gameLinks';
import { useTranslation } from '../../i18n';
import { getGameLinkName, translateBackendError, translateGames } from '../../i18n/helpers';
import { TeamsState, TeamId } from '../../types/teams';
import { gameUsesTeams, getTeamStartBlockReason } from '../../utils/teamGames';
import { HostLobby } from './HostLobby';
import { PlayerLobby } from './PlayerLobby';
import { LobbyReadyFooter } from './LobbyReadyFooter';
import { LobbyQrZoomModal } from './LobbyQrZoomModal';
import { HostJoinAsPlayerModal } from './HostJoinAsPlayerModal';
import type { PremiumButtonAnim } from './LobbyQrPanel';
import type { RoomBranding, CompanyQuestion, AiPromptUsage } from '../../types/game';

type MenuPage = 'how-to-play' | 'premium' | 'support' | null;

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  teamId?: 'A' | 'B';
  isBot?: boolean;
}

interface RoomSettings {
  timerDuration?: number;
  category?: string;
  whosPlaying?: string;
  numberOfQuestions?: number;
}

interface GameSelectionProps {
  roomName: string;
  currentGameType?: string;
  votes?: Record<string, number>;
  categoryVotes?: Record<string, number>;
  players?: Player[];
  teams?: TeamsState;
  onSelectGame: (gameType: string) => void;
  onStartGame: (selectedGameType?: string) => void;
  socket: WebSocketAdapter;
  isHost?: boolean;
  myPlayerId?: string;
  settings?: RoomSettings;
  branding?: RoomBranding | null;
  companyQuestions?: CompanyQuestion[];
  aiPromptUsage?: AiPromptUsage | null;
  onGoHome?: () => void;
  hostIsPlaying?: boolean;
  onHostIsPlayingChange?: (isPlaying: boolean) => void;
  isPremium?: boolean;
  onJoinRoom?: () => void;
}

const getGamesForRoom = (_isPremium?: boolean): GameConfig[] => config.games;

/** Personalized company quiz — shown first when premium room has >20 company questions. */
const COMPANY_QUIZ_GAME: GameConfig = {
  id: 'company-quiz',
  name: 'Company Quiz',
  description: 'Play your company / party / crew questions',
  extendedDescription:
    'Trivia built from your company or event — generated or added in Settings',
  icon: '🏢',
  color: '#2563EB',
  category: 'competitive',
  minPlayers: 1,
};

const GameSelection: React.FC<GameSelectionProps> = ({
  roomName,
  currentGameType,
  votes = {},
  categoryVotes = {},
  players = [],
  teams,
  onSelectGame,
  onStartGame,
  socket,
  isHost = false,
  myPlayerId,
  settings,
  branding,
  companyQuestions,
  aiPromptUsage,
  onGoHome,
  hostIsPlaying = false,
  onHostIsPlayingChange,
  isPremium = false,
  onJoinRoom,
}) => {
  const { t, i18n } = useTranslation();
  const GAMES = useMemo(() => {
    const base = translateGames(getGamesForRoom(isPremium));
    // Company Quiz only when premium and more than 20 company/custom questions
    const hasCompanyQuiz =
      isPremium && (companyQuestions?.length ?? 0) >= 21;
    if (!hasCompanyQuiz) return base.filter((g) => g.id !== 'company-quiz');
    const brand = branding?.displayName?.trim();
    const companyGame: GameConfig = {
      ...COMPANY_QUIZ_GAME,
      name: brand
        ? t('games.company-quiz.nameWithBrand', { name: brand })
        : t('games.company-quiz.name'),
      description: t('games.company-quiz.description'),
      extendedDescription: t('games.company-quiz.extendedDescription'),
    };
    return [companyGame, ...base.filter((g) => g.id !== 'company-quiz')];
  }, [isPremium, companyQuestions, branding?.displayName, i18n.language, t]);

  const [selectedGameType, setSelectedGameType] = useState<string | undefined>(currentGameType);
  const lastBackendGameTypeRef = useRef<string | undefined>(currentGameType);
  const [myCategoryVotes, setMyCategoryVotes] = useState<string[]>([]);
  const [hasVotedBefore, setHasVotedBefore] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [currentMenuPage, setCurrentMenuPage] = useState<MenuPage>(null);
  const { showToast } = useToast();
  const [hostPlayerName, setHostPlayerName] = useState('');
  const [hostAvatar, setHostAvatar] = useState(avatars[0]);
  const [showHostPlayerForm, setShowHostPlayerForm] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [premiumButtonAnim, setPremiumButtonAnim] = useState<PremiumButtonAnim>(null);
  const premiumAnimIndexRef = useRef(0);
  const [pendingAiCount, setPendingAiCount] = useState(0);
  const botCountRef = useRef(players.filter((p) => p.isBot).length);

  const PREMIUM_BUTTON_ANIMS = ['zoom', 'jump', 'shake'] as const;
  const PREMIUM_ANIM_DURATION_MS = 800;
  const PREMIUM_ANIM_INTERVAL_MS = 10000;

  const hostInPlayersList = players.some((p) => p.id === myPlayerId);

  const getGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
    const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      coop: 'blitz',
    };
    return variantMap[appVariant] || 'blitz';
  };

  const gameLinks = getMenuGameLinks(getGameType());

  const menuItems = [
    {
      id: 'play-another-game-header',
      label: t('lobby.menu.playAnotherGamePack'),
      isSectionHeader: true,
    },
    ...gameLinks.map((link) => ({
      id: link.id,
      label: getGameLinkName(link.id),
      href: link.url,
    })),
    { id: 'menu-divider', isDivider: true },
    {
      id: 'menu-section-header',
      label: t('lobby.menu.sectionHeader'),
      isSectionHeader: true,
    },
    {
      id: 'how-to-play',
      label: t('common.howToPlay'),
      onClick: () => setCurrentMenuPage('how-to-play'),
    },
    {
      id: 'premium',
      label: t('common.premium'),
      onClick: () => setCurrentMenuPage('premium'),
    },
    {
      id: 'support',
      label: t('common.support'),
      onClick: () => setCurrentMenuPage('support'),
    },
  ];

  useEffect(() => {
    if (currentGameType && currentGameType !== lastBackendGameTypeRef.current) {
      lastBackendGameTypeRef.current = currentGameType;
      setSelectedGameType(currentGameType);
    }
  }, [currentGameType]);

  // Clear pending AI placeholders as real bots appear (or after timeout)
  useEffect(() => {
    const botCount = players.filter((p) => p.isBot).length;
    const prev = botCountRef.current;
    if (botCount > prev) {
      const gained = botCount - prev;
      setPendingAiCount((n) => Math.max(0, n - gained));
    }
    botCountRef.current = botCount;
  }, [players]);

  useEffect(() => {
    if (pendingAiCount <= 0) return;
    const timeoutId = setTimeout(() => setPendingAiCount(0), 25000);
    return () => clearTimeout(timeoutId);
  }, [pendingAiCount]);

  useEffect(() => {
    if (!isHost || isPremium || !onJoinRoom) return;

    let clearAnimTimer: ReturnType<typeof setTimeout> | undefined;

    const runNextAnim = () => {
      const anim = PREMIUM_BUTTON_ANIMS[premiumAnimIndexRef.current % PREMIUM_BUTTON_ANIMS.length];
      premiumAnimIndexRef.current += 1;
      setPremiumButtonAnim(anim);
      clearAnimTimer = setTimeout(() => setPremiumButtonAnim(null), PREMIUM_ANIM_DURATION_MS);
    };

    runNextAnim();
    const interval = setInterval(runNextAnim, PREMIUM_ANIM_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (clearAnimTimer) clearTimeout(clearAnimTimer);
    };
  }, [isHost, isPremium, onJoinRoom]);

  useEffect(() => {
    if (isHost && hostInPlayersList && !hostIsPlaying && !showHostPlayerForm) {
      onHostIsPlayingChange?.(true);
    } else if (isHost && !hostInPlayersList && hostIsPlaying) {
      onHostIsPlayingChange?.(false);
    }
  }, [isHost, hostInPlayersList, hostIsPlaying, showHostPlayerForm, onHostIsPlayingChange]);

  useEffect(() => {
    const handleCategoryVotesUpdated = (data: {
      roomName: string;
      categoryVotes: Record<string, number>;
      playerCategoryVotes?: string[];
      playerId?: string;
    }) => {
      if (data.playerId === myPlayerId && data.playerCategoryVotes) {
        setMyCategoryVotes(data.playerCategoryVotes);
        if (currentGameType) {
          RoomSessionStorage.setCategoryVotes(roomName, currentGameType, data.playerCategoryVotes);
        }
      }
    };

    const handleCategoryVoteError = (error: { message: string }) => {
      showToast(translateBackendError(error.message), 'warning');
    };

    const handleGameTypeChanged = () => {
      setMyCategoryVotes([]);
      if (currentGameType) {
        RoomSessionStorage.clearCategoryVotes(roomName, currentGameType);
      }
    };

    socket.on('category-votes-updated', handleCategoryVotesUpdated);
    socket.on('category-vote-error', handleCategoryVoteError);
    socket.on('game-type-changed', handleGameTypeChanged);
    return () => {
      socket.off('category-votes-updated', handleCategoryVotesUpdated);
      socket.off('category-vote-error', handleCategoryVoteError);
      socket.off('game-type-changed', handleGameTypeChanged);
    };
  }, [socket, showToast, myPlayerId, roomName, currentGameType]);

  useEffect(() => {
    if (currentGameType && !isHost) {
      const savedVotes = RoomSessionStorage.getCategoryVotes(roomName, currentGameType);
      setHasVotedBefore(savedVotes.length > 0);
    }
  }, [currentGameType, roomName, isHost]);

  if (currentMenuPage === 'how-to-play') {
    return (
      <MenuPageWrapper
        title={t('common.howToPlay')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HowToPlayPage />
      </MenuPageWrapper>
    );
  }

  if (currentMenuPage === 'premium') {
    return (
      <MenuPageWrapper
        title={t('common.premium')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <PremiumPage gameType={getGameType()} />
      </MenuPageWrapper>
    );
  }

  if (currentMenuPage === 'support') {
    return (
      <MenuPageWrapper
        title={t('common.support')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HelpPage />
      </MenuPageWrapper>
    );
  }

  const handleGameSelect = (gameType: string) => {
    const selectedGame = ALL_GAMES.find((g) => g.id === gameType);
    const requiresPremium =
      !isPremium && selectedGame?.premium === true && !isMonthlyFreeGame(gameType);

    if (isHost && requiresPremium) {
      setSelectedGameType(gameType);
      setCurrentMenuPage('premium');
      return;
    }

    setSelectedGameType(gameType);

    if (isHost) {
      socket.emit('change-game-type', { roomName, gameType });
      onSelectGame(gameType);
    } else {
      socket.emit('vote-for-game', { roomName, gameType });
    }
  };

  const gameTypeForTeams = selectedGameType || currentGameType;
  const showTeamAssign = gameUsesTeams(gameTypeForTeams);
  const teamStartBlock = getTeamStartBlockReason(players, gameTypeForTeams);

  const handleSetTeam = (playerId: string, teamId: TeamId | null) => {
    if (!socket || !roomName) return;
    socket.emit('set-player-team', { roomName, playerId, teamId });
  };

  const handleCategoryVote = (category: string) => {
    if (!socket || !roomName || !currentGameType) return;

    if (hasVotedBefore) {
      showToast(t('toasts.alreadyVotedCategories'), 'warning');
      return;
    }

    const isCurrentlyVoted = myCategoryVotes.includes(category);
    let newVotes: string[];
    if (isCurrentlyVoted) {
      newVotes = myCategoryVotes.filter((c) => c !== category);
    } else if (myCategoryVotes.length < 3) {
      newVotes = [...myCategoryVotes, category];
    } else {
      showToast(t('toasts.maxCategoryVotes'), 'warning');
      return;
    }

    setMyCategoryVotes(newVotes);
    RoomSessionStorage.setCategoryVotes(roomName, currentGameType, newVotes);
    socket.emit('vote-for-category', { roomName, category });
  };

  const handleHostIsPlayingToggle = () => {
    if (!hostIsPlaying && !showHostPlayerForm) {
      setShowHostPlayerForm(true);
    } else if (hostIsPlaying || showHostPlayerForm) {
      if (hostInPlayersList) {
        socket.emit('host-leave-as-player', { roomName });
      }
      setShowHostPlayerForm(false);
      RoomSessionStorage.clearPlayerData();
      onHostIsPlayingChange?.(false);
    }
  };

  const handleHostJoinAsPlayer = () => {
    if (!hostPlayerName.trim()) {
      showToast(t('toasts.enterName'), 'warning');
      return;
    }

    RoomSessionStorage.setPlayerName(hostPlayerName.trim());
    RoomSessionStorage.setAvatar(hostAvatar);
    socket.emit('join-room', {
      roomName,
      playerName: hostPlayerName.trim(),
      avatar: hostAvatar,
    });
    setShowHostPlayerForm(false);
    onHostIsPlayingChange?.(true);
  };

  const handleStartGame = () => {
    const humanPlayers = players.filter((p) => !p.isBot);
    if (humanPlayers.length === 0) {
      showToast(t('lobby.ai.needHumanToStart'), 'warning');
      return;
    }
    const gameTypeToStart = selectedGameType || currentGameType;
    if (gameUsesTeams(gameTypeToStart) && teamStartBlock) {
      const toastKey =
        teamStartBlock === 'unassigned'
          ? 'lobby.teams.startNeedAssign'
          : teamStartBlock === 'unbalanced'
            ? 'lobby.teams.startNeedBalance'
            : 'lobby.teams.startNeedPlayers';
      showToast(t(toastKey), 'warning');
      return;
    }
    onStartGame(gameTypeToStart);
  };

  const botCount = players.filter((p) => p.isBot).length;
  const handleAddAiPlayer = () => {
    if (botCount + pendingAiCount >= 3) {
      showToast(t('lobby.ai.maxBots'), 'warning');
      return;
    }
    setPendingAiCount((n) => n + 1);
    socket.emit('spawn-ai-bot', { roomName });
  };

  const handleRemoveAiPlayer = (playerId: string) => {
    socket.emit('remove-ai-bot', { roomName, playerId });
  };

  const startDisabled =
    players.length === 0 ||
    !(selectedGameType || currentGameType) ||
    (((selectedGameType || currentGameType) === 'collaborative-puzzle' ||
      (selectedGameType || currentGameType) === 'story-chain') &&
      players.length < 2) ||
    (showTeamAssign && !!teamStartBlock);

  return (
    <div
      className={`app ${isHost ? 'host-game-selection' : 'player-game-selection'}`}
    >
      {isHost && <BgAnimate speed={2.0} delayBetweenCycles={1000} gameType={currentGameType} />}
      <div className="lobby-page">
        <AppHeader
          logo={null}
          isPremium={isPremium}
          showLanguageSelector
          leftButton={
            onGoHome
              ? {
                  icon: HeaderIcons.back,
                  onClick: onGoHome,
                  tooltip: t('common.backToHome'),
                }
              : undefined
          }
          rightButton={undefined}
          rightContent={
            isHost ? (
              <div className="lobby-toolbar">
                <button
                  type="button"
                  data-testid="statistics-modal-trigger"
                  onClick={() => setShowStatistics(true)}
                  className="lobby-toolbar-btn"
                  title={t('lobby.gameSelection.viewStatistics')}
                >
                  <span className="lobby-toolbar-btn__icon">
                    <AppIcon name="barChart" />
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="game-configuration-button"
                  onClick={() => setShowSettings(true)}
                  className="lobby-toolbar-btn"
                  title={t('lobby.gameSelection.gameConfiguration')}
                >
                  <span className="lobby-toolbar-btn__icon">
                    <AppIcon name="settings" />
                  </span>
                </button>
                <BurgerMenu items={menuItems} />
              </div>
            ) : undefined
          }
        />

        <div className="container">
          {isHost ? (
            <HostLobby
              roomName={roomName}
              games={GAMES}
              currentGameType={currentGameType}
              selectedGameType={selectedGameType}
              votes={votes}
              categoryVotes={categoryVotes}
              players={players}
              teams={teams}
              showTeamAssign={showTeamAssign}
              myPlayerId={myPlayerId}
              isPremium={isPremium}
              hostIsPlaying={hostIsPlaying}
              premiumButtonAnim={premiumButtonAnim}
              brandingDisplayName={branding?.displayName}
              brandingLogoUrl={branding?.logoDataUrl}
              onSelectGame={handleGameSelect}
              onPremiumClick={() => setCurrentMenuPage('premium')}
              onZoomQr={() => setShowQRModal(true)}
              onJoinRoom={onJoinRoom}
              onOpenSettings={() => setShowSettings(true)}
              onToggleHostPlaying={handleHostIsPlayingToggle}
              onSetTeam={handleSetTeam}
              onAddAiPlayer={handleAddAiPlayer}
              onRemoveAiPlayer={handleRemoveAiPlayer}
              addAiDisabled={botCount + pendingAiCount >= 3}
              pendingAiCount={pendingAiCount}
            />
          ) : (
            <PlayerLobby
              roomName={roomName}
              games={GAMES}
              currentGameType={currentGameType}
              categoryVotes={categoryVotes}
              myCategoryVotes={myCategoryVotes}
              players={players}
              teams={teams}
              showTeamAssign={showTeamAssign}
              myPlayerId={myPlayerId}
              hasVotedBefore={hasVotedBefore}
              brandingDisplayName={branding?.displayName}
              brandingLogoUrl={branding?.logoDataUrl}
              onSetTeam={handleSetTeam}
              onCategoryVote={handleCategoryVote}
            />
          )}
        </div>

        {isHost && (
          <LobbyReadyFooter
            playerCount={players.length}
            disabled={startDisabled}
            isPremium={isPremium}
            onPremiumClick={() => setCurrentMenuPage('premium')}
            onStart={handleStartGame}
          />
        )}

        {isHost && (
          <>
            <SettingsModal
              isOpen={showSettings}
              onClose={() => setShowSettings(false)}
              socket={socket}
              roomName={roomName}
              currentSettings={settings}
              currentBranding={branding}
              companyQuestions={companyQuestions}
              aiPromptUsage={aiPromptUsage}
              isPremium={isPremium}
              onGoPremium={() => {
                setShowSettings(false);
                setCurrentMenuPage('premium');
              }}
              onHavePremiumCode={onJoinRoom}
            />
            <StatisticsModal
              isOpen={showStatistics}
              onClose={() => setShowStatistics(false)}
              roomName={roomName}
            />
          </>
        )}

        <LobbyQrZoomModal
          isOpen={showQRModal}
          roomName={roomName}
          onClose={() => setShowQRModal(false)}
        />

        <HostJoinAsPlayerModal
          isOpen={showHostPlayerForm && !hostIsPlaying}
          playerName={hostPlayerName}
          avatar={hostAvatar}
          onPlayerNameChange={setHostPlayerName}
          onAvatarChange={setHostAvatar}
          onClose={() => setShowHostPlayerForm(false)}
          onJoin={handleHostJoinAsPlayer}
        />
      </div>
    </div>
  );
};

export default GameSelection;
